'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { Box, Container, Typography } from '@mui/material';
import { NEWS_PATH } from '@/consts/routes';
import parseNewsArticleBody from '@/utils/parseNewsArticleBody';
import { getBlogTitleStyleSx } from './blog-title-styles';
import NewsArticleBody from './NewsArticleBody';
import NewsArticleImageCarousel from './NewsArticleImageCarousel';

/** Marketing interior nav height — keep in sync with nav bar. */
const NEWS_NAV_HEIGHT = { xs: '4.5rem', md: '5.25rem' };

const backLinkSx = {
  display: 'inline-block',
  textDecoration: 'none',
  color: 'inherit',
  fontWeight: 500,
  borderBottom: '1px solid currentColor',
  '&:hover': { opacity: 0.7 },
};

function ArticleMetadata({ article }) {
  const items = [article.date, article.source].filter(Boolean);
  if (items.length === 0) {
    return null;
  }

  return (
    <Typography
      component="p"
      variant="body1"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: 1.5,
        rowGap: 0.5,
        m: 0,
      }}
    >
      {items.map((item, index) => (
        <Box
          key={item}
          component="span"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}
        >
          {index > 0 ? (
            <Box component="span" aria-hidden sx={{ color: 'text.secondary' }}>
              ·
            </Box>
          ) : null}
          {item}
        </Box>
      ))}
    </Typography>
  );
}

ArticleMetadata.propTypes = {
  article: PropTypes.shape({
    date: PropTypes.string,
    source: PropTypes.string.isRequired,
  }).isRequired,
};

function ArticleHeader({ article, sx, titleStyle }) {
  const titleSx = titleStyle
    ? { fontWeight: 500, mb: 1.5, ...getBlogTitleStyleSx(titleStyle) }
    : { fontWeight: 500, mb: 1.5 };

  return (
    <Box sx={{ flexShrink: 0, maxWidth: 900, ...sx }}>
      <Typography component="h1" variant="h4" sx={titleSx}>
        {article.title}
      </Typography>
      <ArticleMetadata article={article} />
    </Box>
  );
}

ArticleHeader.propTypes = {
  article: PropTypes.shape({
    title: PropTypes.string.isRequired,
    date: PropTypes.string,
    source: PropTypes.string.isRequired,
  }).isRequired,
  sx: PropTypes.object,
  titleStyle: PropTypes.string,
};

export default function NewsDetailPage({
  article,
  listPath = NEWS_PATH,
  backLabel = 'Back to News',
  titleStyle,
}) {
  const { proseHtml, videos, images } = useMemo(
    () => parseNewsArticleBody(article.bodyHtml),
    [article.bodyHtml],
  );
  const hasCarousel = images.length > 0;
  const hasBelowFoldBody = Boolean(proseHtml) || videos.length > 0;

  if (hasCarousel) {
    return (
      <>
        <Box
          component="section"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            height: {
              xs: `calc(100dvh - ${NEWS_NAV_HEIGHT.xs})`,
              md: `calc(100dvh - ${NEWS_NAV_HEIGHT.md})`,
            },
            boxSizing: 'border-box',
            pt: { xs: 2, md: 2.5 },
            pb: { xs: 2, md: 2.5 },
          }}
        >
          <Container
            maxWidth="lg"
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <ArticleHeader
              article={article}
              titleStyle={titleStyle}
              sx={{ mb: { xs: 1.5, md: 2 } }}
            />

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <NewsArticleImageCarousel images={images} title={article.title} fillHeight />
            </Box>
          </Container>
        </Box>

        {hasBelowFoldBody ? (
          <Box component="section" sx={{ py: { xs: 4, md: 5 } }}>
            <Container maxWidth="lg">
              <NewsArticleBody
                proseHtml={proseHtml}
                videos={videos}
                images={[]}
                title={article.title}
              />
            </Container>
          </Box>
        ) : null}

        <Box component="section" sx={{ pb: { xs: 6, md: 10 } }}>
          <Container maxWidth="lg">
            <Box component={Link} href={listPath} sx={backLinkSx}>
              {backLabel}
            </Box>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box component="section" sx={{ pt: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <ArticleHeader article={article} titleStyle={titleStyle} sx={{ mb: 3 }} />
        </Container>
      </Box>

      <Box
        component="section"
        sx={{
          height: { xs: 280, sm: 360, md: 420 },
          backgroundImage: `url(${article.bannerSrc})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          mb: { xs: 4, md: 6 },
        }}
        aria-hidden
      />

      <Box component="section" sx={{ pb: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          {article.bodyHtml ? (
            <Box sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
              <NewsArticleBody
                proseHtml={proseHtml}
                videos={videos}
                images={images}
                title={article.title}
              />
            </Box>
          ) : null}

          <Box sx={{ mt: 6 }}>
            <Box component={Link} href={listPath} sx={backLinkSx}>
              {backLabel}
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
}

NewsDetailPage.propTypes = {
  article: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    source: PropTypes.string.isRequired,
    date: PropTypes.string,
    category: PropTypes.string,
    bannerSrc: PropTypes.string.isRequired,
    bodyHtml: PropTypes.string,
  }).isRequired,
  listPath: PropTypes.string,
  backLabel: PropTypes.string,
  titleStyle: PropTypes.string,
};
